import { useTranslation } from "react-i18next";
import {
  buildAvailableTimeBucketOptions,
  buildPastAnalyticsRows,
  buildPastCatchTimelineBuckets,
  buildPastConditionsTimeline,
  filterPastAnalyticsEvents,
  formatPastConditionValue,
  parseTripDate,
  toFiniteNumber,
} from "../pastTripAnalytics";

const EMPTY_ANALYTICS_FILTERS = {
  species: "",
  wayOfFishing: "",
  typeOfFishing: "",
  style: "",
  technique: "",
  bait: "",
  lure: "",
  timeBucket: "",
};

function normalizeRecordedValue(value) {
  return typeof value === "string" ? value.trim() : "";
}

function getCatchRecord(item) {
  return item?.catch && typeof item.catch === "object" ? item.catch : item || {};
}

function buildRecordedCounts(items, getValues, limit = 5) {
  const grouped = new Map();

  items.forEach((item) => {
    const values = getValues(getCatchRecord(item), item);
    const normalizedValues = (Array.isArray(values) ? values : [values])
      .map(normalizeRecordedValue)
      .filter(Boolean);

    normalizedValues.forEach((value) => {
      const key = value.toLowerCase();
      const existing = grouped.get(key);
      if (existing) {
        existing.count += 1;
      } else {
        grouped.set(key, { value, count: 1 });
      }
    });
  });

  return Array.from(grouped.values())
    .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value))
    .slice(0, limit);
}

function buildVisiblePastConditionMetrics(timeline, metricDefs) {
  return metricDefs
    .map((metric) => {
      const samples = timeline
        .map((day, index) => {
          const value = metric.getValue(day);
          if (!Number.isFinite(value)) return null;

          return {
            index,
            date: day.date,
            value,
          };
        })
        .filter(Boolean);

      if (samples.length === 0) {
        return null;
      }

      const values = samples.map((sample) => sample.value);
      const min = Math.min(...values);
      const max = Math.max(...values);

      return {
        ...metric,
        samples,
        min,
        max,
        rangeLabel:
          min === max
            ? formatPastConditionValue(min, metric.precision, metric.unit)
            : `${formatPastConditionValue(min, metric.precision, metric.unit)} to ${formatPastConditionValue(
                max,
                metric.precision,
                metric.unit
              )}`,
      };
    })
    .filter(Boolean);
}

export default function usePastTripSummaryView({
  trip,
  summary,
  weatherConditions,
  analyticsView,
  setAnalyticsView,
  analyticsFilters,
  setAnalyticsFilters,
}) {
  const { t } = useTranslation();

  const summaryItems = summary?.items || [];
  const summarySpeciesCounts = summary?.speciesCounts || [];
  const summaryByWeight = summary?.byWeight || [];
  const summaryGalleryItems = summaryItems.filter((item) => item?.mediaUrl);
  const summaryAnalytics = summary?.analytics || null;
  const analyticsEvents = Array.isArray(summaryAnalytics?.events)
    ? summaryAnalytics.events
    : [];
  const filteredAnalyticsEvents = filterPastAnalyticsEvents(
    analyticsEvents,
    analyticsFilters
  );
  const translateAnalyticsLabel = (item) =>
    item?.labelKey ? { ...item, label: t(item.labelKey) } : item;
  const analyticsRows = buildPastAnalyticsRows(
    filteredAnalyticsEvents,
    analyticsView
  ).map(translateAnalyticsLabel);
  const analyticsTimeBucketOptions =
    buildAvailableTimeBucketOptions(analyticsEvents).map(
      translateAnalyticsLabel
    );
  const analyticsFilterOptions = {
    species: Array.isArray(summaryAnalytics?.availableFilters?.species)
      ? summaryAnalytics.availableFilters.species
      : [],
    wayOfFishing: Array.isArray(summaryAnalytics?.availableFilters?.wayOfFishing)
      ? summaryAnalytics.availableFilters.wayOfFishing
      : [],
    typeOfFishing: Array.isArray(summaryAnalytics?.availableFilters?.typeOfFishing)
      ? summaryAnalytics.availableFilters.typeOfFishing
      : [],
    style: Array.isArray(summaryAnalytics?.availableFilters?.style)
      ? summaryAnalytics.availableFilters.style
      : [],
    technique: Array.isArray(summaryAnalytics?.availableFilters?.technique)
      ? summaryAnalytics.availableFilters.technique
      : [],
    bait: Array.isArray(summaryAnalytics?.availableFilters?.bait)
      ? summaryAnalytics.availableFilters.bait
      : [],
    lure: Array.isArray(summaryAnalytics?.availableFilters?.lure)
      ? summaryAnalytics.availableFilters.lure
      : [],
  };
  const analyticsVisibleFilters = [
    {
      key: "species",
      label: t("fish", { defaultValue: "Fish" }),
      options: analyticsFilterOptions.species,
    },
    {
      key: "wayOfFishing",
      label: t("trip_way_of_fishing", { defaultValue: "Way of Fishing" }),
      options: analyticsFilterOptions.wayOfFishing,
    },
    {
      key: "typeOfFishing",
      label: t("trip_type_of_fishing", { defaultValue: "Type of Fishing" }),
      options: analyticsFilterOptions.typeOfFishing,
    },
    {
      key: "style",
      label: t("style", { defaultValue: "Style" }),
      options: analyticsFilterOptions.style,
    },
    {
      key: "technique",
      label: t("technique", { defaultValue: "Technique" }),
      options: analyticsFilterOptions.technique,
    },
    {
      key: "bait",
      label: t("bait", { defaultValue: "Bait" }),
      options: analyticsFilterOptions.bait,
    },
    {
      key: "lure",
      label: t("lure", { defaultValue: "Lure" }),
      options: analyticsFilterOptions.lure,
    },
    {
      key: "timeBucket",
      label: t("trip_time_bucket", { defaultValue: "Time Bucket" }),
      options: analyticsTimeBucketOptions,
    },
  ].filter((filter) => filter.options.length > 0);
  const analyticsUniqueDays = new Set(
    filteredAnalyticsEvents
      .map((event) => (typeof event?.tripDate === "string" ? event.tripDate.trim() : ""))
      .filter(Boolean)
  ).size;
  const analyticsMaxCount = analyticsRows.reduce(
    (max, row) => Math.max(max, row.count || 0),
    0
  );
  const hasAnalyticsData = analyticsEvents.length > 0;
  const hasActiveAnalyticsFilters = Object.values(analyticsFilters).some(Boolean);
  const filteredEventIds = new Set(
    filteredAnalyticsEvents.map((event) => event?.id).filter(Boolean)
  );
  const filteredSummaryItems = hasActiveAnalyticsFilters
    ? summaryItems.filter((item) => filteredEventIds.has(item?.id))
    : summaryItems;
  const biggestRecordedCatch = summaryByWeight.find((item) =>
    Number.isFinite(item?.weightSortKg)
  );
  const activeCatchDays = new Set(
    summaryItems.map((item) => item?.savedCatchDate).filter(Boolean)
  ).size;
  const recordedSetupGroups = [
    { key: "rod", icon: "rod", rows: buildRecordedCounts(filteredSummaryItems, (item) => item.rod) },
    { key: "reel", icon: "reel", rows: buildRecordedCounts(filteredSummaryItems, (item) => item.reel) },
    { key: "line", icon: "line-braid", rows: buildRecordedCounts(filteredSummaryItems, (item) => item.braid) },
    { key: "leader", icon: "leader", rows: buildRecordedCounts(filteredSummaryItems, (item) => item.leader) },
    {
      key: "hook",
      icon: "hook",
      rows: buildRecordedCounts(filteredSummaryItems, (item) =>
        [item.hookBrand, item.hookType, item.hookSize].filter(Boolean).join(" ")
      ),
    },
  ].filter((group) => group.rows.length > 0);
  const lureAndBaitRows = [
    ...buildRecordedCounts(filteredSummaryItems, (item) => item.lure).map((row) => ({
      ...row,
      type: "lure",
    })),
    ...buildRecordedCounts(filteredSummaryItems, (item) => item.bait).map((row) => ({
      ...row,
      type: "bait",
    })),
  ].sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
  const recordedMethodGroups = [
    { key: "wayOfFishing", rows: buildRecordedCounts(filteredSummaryItems, (item) => item.wayOfFishing) },
    { key: "typeOfFishing", rows: buildRecordedCounts(filteredSummaryItems, (item) => item.typeOfFishing) },
    { key: "style", rows: buildRecordedCounts(filteredSummaryItems, (item) => item.style) },
    { key: "technique", rows: buildRecordedCounts(filteredSummaryItems, (item) => item.technique) },
  ].filter((group) => group.rows.length > 0);
  const recordedLocationRows = buildRecordedCounts(
    filteredSummaryItems,
    (catchRecord, item) => item?.location || catchRecord.location
  );
  const pastConditionTimeline = buildPastConditionsTimeline(
    trip?.startDate,
    trip?.endDate,
    weatherConditions?.days
  );
  const metricDefs = [
    {
      key: "waterTemp",
      label: t("trip_conditions_water_temp", {
        defaultValue: "Water Temp",
      }),
      color: "#0f9ec7",
      unit: "\u00B0C",
      precision: 1,
      getValue: (day) => toFiniteNumber(day?.waterTemp),
    },
    {
      key: "tempMax",
      label: t("trip_conditions_air_temp", {
        defaultValue: "Air Temp (High)",
      }),
      color: "#f08a24",
      unit: "\u00B0C",
      precision: 1,
      getValue: (day) => toFiniteNumber(day?.tempMax),
    },
    {
      key: "windSpeed",
      label: t("trip_conditions_wind_speed", {
        defaultValue: "Wind Speed",
      }),
      color: "#6558d3",
      unit: " km/h",
      precision: 1,
      getValue: (day) => toFiniteNumber(day?.windSpeed),
    },
    {
      key: "moonIllumination",
      label: t("trip_conditions_moon_illumination", {
        defaultValue: "Moon Illumination",
      }),
      color: "#62a53a",
      unit: "%",
      precision: 0,
      getValue: (day) => toFiniteNumber(day?.moon?.illumination),
    },
  ];
  const visiblePastConditionMetrics = buildVisiblePastConditionMetrics(
    pastConditionTimeline,
    metricDefs
  );
  const tripTimelineStartDate = parseTripDate(trip?.startDate);
  const tripTimelineEndDate = parseTripDate(trip?.endDate || trip?.startDate);
  const tripTimelineStartMs = tripTimelineStartDate
    ? new Date(
        tripTimelineStartDate.getFullYear(),
        tripTimelineStartDate.getMonth(),
        tripTimelineStartDate.getDate(),
        0,
        0,
        0,
        0
      ).getTime()
    : null;
  const tripTimelineEndMs = tripTimelineEndDate
    ? new Date(
        tripTimelineEndDate.getFullYear(),
        tripTimelineEndDate.getMonth(),
        tripTimelineEndDate.getDate(),
        23,
        59,
        59,
        999
      ).getTime()
    : null;
  const pastCatchTimelineBuckets = buildPastCatchTimelineBuckets(
    filteredAnalyticsEvents
  );
  const hasPastCatchTimelineChart =
    pastCatchTimelineBuckets.length > 0 &&
    Number.isFinite(tripTimelineStartMs) &&
    Number.isFinite(tripTimelineEndMs) &&
    tripTimelineEndMs > tripTimelineStartMs;
  const analyticsViewOptions = [
    {
      value: "day",
      label: t("trip_catches_by_day", {
        defaultValue: "Catches by Day",
      }),
    },
    {
      value: "timeBucket",
      label: t("trip_catches_by_time", {
        defaultValue: "Catches by Time",
      }),
    },
    {
      value: "speciesByDay",
      label: t("trip_species_by_day", {
        defaultValue: "Species by Day",
      }),
    },
  ];

  const clearAnalyticsFilters = () =>
    setAnalyticsFilters((prev) => ({
      ...prev,
      ...EMPTY_ANALYTICS_FILTERS,
    }));

  const setAnalyticsFilterValue = (key, value) =>
    setAnalyticsFilters((prev) => ({
      ...prev,
      [key]: value,
    }));

  const setAnalyticsViewValue = (value) => setAnalyticsView(value);

  return {
    summaryItems,
    summarySpeciesCounts,
    summaryByWeight,
    summaryGalleryItems,
    totalRecordedCatches: summaryItems.length,
    distinctSpeciesCount: summarySpeciesCounts.length,
    biggestRecordedCatch,
    activeCatchDays,
    autoCollectedCount: summaryItems.filter((item) => item?.sourceType === "post").length,
    manualCatchCount: summaryItems.filter((item) => item?.sourceType === "trip_catch").length,
    filteredSummaryItems,
    recordedSetupGroups,
    lureAndBaitRows,
    recordedMethodGroups,
    recordedLocationRows,
    filteredAnalyticsEvents,
    analyticsRows,
    analyticsVisibleFilters,
    analyticsViewOptions,
    analyticsUniqueDays,
    analyticsMaxCount,
    hasAnalyticsData,
    hasActiveAnalyticsFilters,
    pastConditionTimeline,
    visiblePastConditionMetrics,
    pastCatchTimelineBuckets,
    tripTimelineStartMs,
    tripTimelineEndMs,
    hasPastCatchTimelineChart,
    clearAnalyticsFilters,
    setAnalyticsFilterValue,
    setAnalyticsViewValue,
  };
}
